import React, { Children, ReactNode, useState } from 'react';
import { Button, ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useActionsButtonStyles, useActionsMenuItemStyles } from './actions-button.styles';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { LoadedActionsButtonItem } from './actions-button.types';
import { MoreHoriz } from '@mui/icons-material';
import { CheckMyPermissionDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Colors } from '../../../theme/colors';

export interface ActionsButtonProps {
  itemArray: LoadedActionsButtonItem[];
  isMini?: boolean;
  variant?: 'dark' | 'light';
}

export const ActionsButton = ({ itemArray, isMini, variant }: ActionsButtonProps) => {
  // If there are no items, don't render anything (no button is visible)
  if (itemArray.length === 0) {
    return null;
  }

  const allRequiredPermissions = itemArray
    .map((item) => item.permission)
    .filter((it): it is CheckMyPermissionDto => !!it);

  const notAllItemsRequirePermissions = itemArray.some((item) => !item.permission && !item.availableUiFeatures);

  return (
    <PermissionBoundary
      permissions={allRequiredPermissions}
      onlyRequireSomePermissions
      skipPermissionsChecks={notAllItemsRequirePermissions}
    >
      <ToggleButton isMini={isMini} variant={variant}>
        {itemArray.map((item) => (
          <ActionsMenuItem key={item.title} item={item} />
        ))}
      </ToggleButton>
    </PermissionBoundary>
  );
};

function ToggleButton({
  children,
  isMini,
  variant
}: {
  children: ReactNode;
  isMini: ActionsButtonProps['isMini'];
  variant?: ActionsButtonProps['variant'];
}) {
  const [open, setOpen] = useState(false);
  const classes = useActionsButtonStyles({ variant });
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);

  if (Children.count(children) === 0) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event: any) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <>
      {isMini ? (
        <IconButton className={classes.Mini} ref={anchorRef} onClick={handleToggle}>
          <MoreHoriz />
        </IconButton>
      ) : (
        <Button color="inherit" className={classes.ActionsButton} ref={anchorRef} onClick={handleToggle}>
          <span>Actions</span>
          <KeyboardArrowDownIcon />
        </Button>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        style={{ zIndex: 999 }}
        placement="bottom-end"
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom-end' ? 'end top' : 'end bottom' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                  {children}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

function ActionsMenuItem({
  item: { Icon, title, action, render, permission, availableUiFeatures, dividerBefore }
}: {
  item: LoadedActionsButtonItem;
}) {
  const classes = useActionsMenuItemStyles();

  return (
    <PermissionBoundary permissions={permission ? [permission] : undefined} availableUiFeatures={availableUiFeatures}>
      <MenuItem
        className={dividerBefore ? classes.dividerBefore : undefined}
        onClick={() => {
          if (action) action();
        }}
      >
        {render ? (
          <>{render()}</>
        ) : (
          <>
            {Icon ? <Icon style={{ fill: Colors.midGray, color: Colors.midGray }} /> : null} &nbsp; {title || null}
          </>
        )}
      </MenuItem>
    </PermissionBoundary>
  );
}
